import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Box, Typography, TextField, IconButton, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination, Chip, Button } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckSharpIcon from '@mui/icons-material/CheckSharp'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import MailOutlinedIcon from '@mui/icons-material/MailOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import PasswordOutlinedIcon from '@mui/icons-material/PasswordOutlined'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import ApiService from '../services/ApiService'
import UsageService from '../services/UsageService'
import { validateName, validatePassword } from '../utils/validateForm'

const PM_LLM_QUOTA_ADMIN_EMAILS = new Set([
  'a.karnaukh@mobidev.biz',
  'a.kostiuchenko@mobidev.biz',
])

const Profile = () => {
  const [user, setUser] = useState(null)
  const [environment, setEnvironment] = useState(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingPassword, setIsEditingPassword] = useState(false)
  const [newName, setNewName] = useState(null)
  const [nameError, setNameError] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [currentPasswordError, setCurrentPasswordError] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [projectsList, setProjectsList] = useState([])
  const [activeTab, setActiveTab] = useState(0)
  const [usageHistory, setUsageHistory] = useState([])
  const [usagePage, setUsagePage] = useState(1)
  const [usageTotalPages, setUsageTotalPages] = useState(0)
  const [usageTotalMonths, setUsageTotalMonths] = useState(0)
  const [unlimitedEmails, setUnlimitedEmails] = useState([])
  const [unlimitedEmailsDraft, setUnlimitedEmailsDraft] = useState('')
  const [isSavingUnlimitedEmails, setIsSavingUnlimitedEmails] = useState(false)
  const [unlimitedEmailsError, setUnlimitedEmailsError] = useState('')
  const [unlimitedEmailsSuccess, setUnlimitedEmailsSuccess] = useState('')
  const usagePageSize = 5
  const canManageLlmQuotaOverrides =
    environment === 'production' &&
    PM_LLM_QUOTA_ADMIN_EMAILS.has(String(user?.email || '').trim().toLowerCase())

  const syncUnlimitedEmailsState = (emails = []) => {
    const normalizedEmails = Array.isArray(emails) ? emails : []
    setUnlimitedEmails(normalizedEmails)
    setUnlimitedEmailsDraft(normalizedEmails.join('\n'))
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        let [userData, projectsData, settingsData] = await Promise.all([
          ApiService.get('/auth/profile'),
          ApiService.get('/projects'),
          ApiService.get('/settings'),
        ])
        setUser(userData)
        setEnvironment(settingsData?.environment || null)
        setNewName(userData.full_name)
        setProjectsList(Array.isArray(projectsData?.projects) ? projectsData.projects : [])

        const isQuotaAdmin =
          settingsData?.environment === 'production' &&
          PM_LLM_QUOTA_ADMIN_EMAILS.has(String(userData?.email || '').trim().toLowerCase())

        if (isQuotaAdmin) {
          const quotaSettings = await ApiService.get('/auth/llm-quota-unlimited-emails')
          syncUnlimitedEmailsState(quotaSettings?.emails || [])
        }
      } catch (e) {
        console.error('Failed to load profile/projects', e)
      }
    }
    loadData()
  }, [])

  useEffect(() => {
    if (activeTab === 1) {
      const fetchUsageHistory = async () => {
        try {
          const res = await UsageService.getUsageHistory(usagePage, usagePageSize)
          setUsageHistory(res.months || [])
          setUsageTotalPages(res.totalPages || 0)
          setUsageTotalMonths(res.totalMonths || 0)
        } catch (error) {
          console.error('Error fetching usage history:', error)
        }
      }
      fetchUsageHistory()
    }
  }, [activeTab, usagePage])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleUsagePageChange = (event, value) => {
    setUsagePage(value)
  }

  const handleEditName = () => {
    setIsEditingName(true)
  }

  const handleNameChange = (e) => {
    setNewName(e.target.value)
    if (nameError) {
      setNameError('')
    }
  }

  const handleSaveName = async () => {
    if (!validateName(newName)) {
      setNameError('Required field.')
      return
    } else {
      setNameError('')
    }

    setUser((prevUser) => ({ ...prevUser, full_name: newName }))
    await ApiService.put('/auth/profile', { email: user.email, full_name: newName })
    setIsEditingName(false)
  }

  const handleEditPassword = () => {
    setIsEditingPassword(true)
  }

  const handleCurrentPasswordChange = (e) => {
    setCurrentPassword(e.target.value)
    if (currentPasswordError) {
      setCurrentPasswordError('')
    }
  }

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value)
    if (newPasswordError) {
      setNewPasswordError('')
    }
  }

  const handleClosePassword = async () => {
    setIsEditingPassword(false)
    setCurrentPasswordError('')
    setNewPasswordError('')
  }

  const handleSavePassword = async () => {
    let valid = true

    if (!validatePassword(currentPassword)) {
      setCurrentPasswordError('Invalid password. Minimum length 6.')
      valid = false
    } else {
      setCurrentPasswordError('')
    }

    if (!validatePassword(newPassword)) {
      valid = false
      setNewPasswordError('Invalid password. Minimum length 6.')
      return
    } else {
      setNewPasswordError('')
    }

    if (newPassword === currentPassword) {
      valid = false
      setNewPasswordError('New password must be different from current')
      return
    } else {
      setNewPasswordError('')
    }

    if (!valid) return

    if (currentPassword.length >= 6 && newPassword.length >= 6 && currentPassword !== newPassword) {
      try {
        await ApiService.put('/auth/change-password', { currentPassword, newPassword })
        setIsEditingPassword(false)
        setCurrentPassword('')
        setNewPassword('')
      } catch (error) {
        console.log(error)
        if (error.response.data.error === "Current password is incorrect") {
          setCurrentPasswordError("Current password is incorrect")
        } else {
          console.log(error.response.data.error)
        }
      }
    }
  }

  const handleUnlimitedEmailsChange = (event) => {
    setUnlimitedEmailsDraft(event.target.value)
    if (unlimitedEmailsError) {
      setUnlimitedEmailsError('')
    }
    if (unlimitedEmailsSuccess) {
      setUnlimitedEmailsSuccess('')
    }
  }

  const handleSaveUnlimitedEmails = async () => {
    setIsSavingUnlimitedEmails(true)
    setUnlimitedEmailsError('')
    setUnlimitedEmailsSuccess('')

    try {
      const emails = unlimitedEmailsDraft
        .split(/\r?\n/)
        .map((email) => email.trim())
        .filter(Boolean)

      const result = await ApiService.patch('/auth/llm-quota-unlimited-emails', {
        emails,
      })

      syncUnlimitedEmailsState(result?.emails || [])
      setUnlimitedEmailsSuccess('Unlimited email list saved.')
    } catch (error) {
      setUnlimitedEmailsError(
        error?.response?.data?.error || 'Failed to save unlimited email list.'
      )
    } finally {
      setIsSavingUnlimitedEmails(false)
    }
  }


  return (
    <Box className="profile rootWrapper">
      <Helmet>
        <title>Treegress - Profile</title>
      </Helmet>
      <Header title="Profile" />
      <Box className="srollContent">
        <Sidebar projectsListLength={projectsList.length} />
        {user && <Box className="profileContent">
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Profile Information</Typography>
          </Box>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              mb: 3,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4574D2',
              },
            }}
          >
            <Tab label="Profile" />
            <Tab label="Usage History" />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '72px' }}>
                <MailOutlinedIcon sx={{ mr: '8px' }} />
                <Typography sx={{ width: '160px', m: 0, fontSize: '1.25rem' }}>Work Email:</Typography>
                <Typography sx={{ width: '300px', m: 0, fontSize: '1.25rem' }}>{user.email}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '72px' }}>
                <BadgeOutlinedIcon sx={{ mr: '8px' }} />
                <Typography sx={{ width: '160px', m: 0, fontSize: '1.25rem' }}>Full Name:</Typography>
                {isEditingName ? (
                  <TextField
                    size='small'
                    sx={{ width: '300px', height: '40px', m: 0, fontSize: '1.25rem' }}
                    value={newName}
                    onChange={handleNameChange}
                    required
                    error={!!nameError}
                    helperText={nameError + ' '}
                  />
                ) : (
                  <Typography size='small' sx={{ width: '300px', m: 0, fontSize: '1.25rem' }}>{user.full_name}</Typography>
                )}
                <IconButton onClick={isEditingName ? handleSaveName : handleEditName} sx={{ ml: 0.5 }}>
                  {isEditingName ? <CheckSharpIcon color='primary' /> : <EditOutlinedIcon color='primary' />}
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '72px' }}>
                <PasswordOutlinedIcon sx={{ mr: '8px' }} />
                <Typography sx={{ width: '160px', m: 0, fontSize: '1.25rem' }}>Password:</Typography>
                {isEditingPassword ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '72px' }}>
                    <TextField
                      label="Current Password"
                      type="password"
                      size='small'
                      value={currentPassword}
                      onChange={handleCurrentPasswordChange}
                      sx={{ width: '300px', height: '40px', m: 0, fontSize: '1.25rem', mr: 2 }}
                      required
                      error={!!currentPasswordError}
                      helperText={currentPasswordError + ' '}
                    />
                    <TextField
                      label="New Password"
                      type="password"
                      size='small'
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                      sx={{ width: '300px', height: '40px', m: 0, fontSize: '1.25rem' }}
                      required
                      error={!!newPasswordError}
                      helperText={newPasswordError + ' '}
                    />
                    <IconButton onClick={handleSavePassword} sx={{ ml: 0.5 }}>
                      <CheckSharpIcon color='primary' />
                    </IconButton>
                    <IconButton onClick={handleClosePassword} sx={{ ml: 0.5 }}>
                      <CloseOutlinedIcon color='primary' />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex' }}>
                    {user.hasPassword ?
                      <>
                        <Box sx={{ width: '300px', height: '40px', display: 'flex', alignItems: 'center' }}>
                          <span style={{ height: '16px' }}>******</span>
                        </Box>
                        <IconButton onClick={handleEditPassword} sx={{ ml: 0.5 }}>
                          <EditOutlinedIcon color='primary' />
                        </IconButton>
                      </>
                      :
                      <IconButton disabled sx={{ ml: 0.5 }}>
                        <EditOutlinedIcon />
                      </IconButton>
                    }
                  </Box>
                )}
              </Box>

              {canManageLlmQuotaOverrides && (
                <Box
                  sx={{
                    mt: 4,
                    pt: 3,
                    maxWidth: '720px',
                    borderTop: '1px solid #D7E2EC',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Unlimited LLM Quota
                  </Typography>
                  <Typography sx={{ mb: 2, color: '#667085' }}>
                    This section is visible only for your production account. Enter one email per line.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    value={unlimitedEmailsDraft}
                    onChange={handleUnlimitedEmailsChange}
                    placeholder={'user@example.com\nanother.user@example.com'}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveUnlimitedEmails}
                      disabled={isSavingUnlimitedEmails}
                    >
                      {isSavingUnlimitedEmails ? 'Saving...' : 'Save emails'}
                    </Button>
                    <Typography sx={{ color: '#667085' }}>
                      Managed emails: {unlimitedEmails.length}
                    </Typography>
                  </Box>
                  {unlimitedEmailsError ? (
                    <Typography sx={{ mt: 1.5, color: '#D32F2F' }}>
                      {unlimitedEmailsError}
                    </Typography>
                  ) : null}
                  {unlimitedEmailsSuccess ? (
                    <Typography sx={{ mt: 1.5, color: '#198754' }}>
                      {unlimitedEmailsSuccess}
                    </Typography>
                  ) : null}
                </Box>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box className="usageHistoryTab">
              <Typography variant="h6" sx={{ mb: 2 }}>
                Monthly Executions History
              </Typography>
              <TableContainer component={Paper} className="usageHistoryTableContainer" sx={{ boxShadow: 'none', border: '1px solid #D7E2EC' }}>
                <Table className="usageHistoryTable" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: '50%' }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '25%', textAlign: 'center' }}>Executions</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '25%' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageHistory.length > 0 ? (
                      usageHistory.map((monthData) => (
                        <>
                          <TableRow key={`${monthData.period}-total`} sx={{ backgroundColor: 'rgba(45, 67, 86, 0.06)' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                              {monthData.period} — Total
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', textAlign: 'center' }}>
                              {monthData.totalExecutions}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                          {monthData.projects.map((project) => (
                            <TableRow key={`${monthData.period}-${project.project_id}`} hover>
                              <TableCell sx={{ pl: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography sx={{ mr: 1, color: '#757575', fontSize: '1rem' }}>└</Typography>
                                  <Typography sx={{ fontSize: '0.875rem' }}>{project.project_name}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {project.executions}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={project.status}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    backgroundColor: project.status === 'Active'
                                      ? 'rgba(25, 135, 84, 0.1)'
                                      : 'rgba(211, 47, 47, 0.1)',
                                    color: project.status === 'Active'
                                      ? '#198754'
                                      : '#D32F2F',
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body1" align="center" sx={{ py: 4, color: '#757575' }}>
                            No usage data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {usageTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}>
                  <Typography variant="body2" mr={1}>
                    {`Page ${usagePage} of ${usageTotalPages} (${usageTotalMonths} months)`}
                  </Typography>
                  <Pagination
                    count={usageTotalPages}
                    page={usagePage}
                    onChange={handleUsagePageChange}
                    shape="rounded"
                    color='primary'
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>}
      </Box>
    </Box>
  )
}

export default Profile
